import { FmlxColumn, FmlxColumnFilter } from 'business/models/CustomModels';
import {
  PFDocProteinFormulationIngredientsDTO,
  PFStateDTO,
  ProteinFormulationIngredientTypeDTO,
} from 'business/models/Models';
import ButtonsListContainer from 'components/Button/ButtonsListContainer';
import {
  COPTYPESTORE,
  CopyTypes,
  IndexDBBuffer,
  PROTEINSTORE,
} from 'components/plateviewer/experiment/IndexDBBuffer';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { FmlxDropdown, FmlxTable } from 'fmlx-common-ui';
import React, { useCallback, useEffect, useMemo, memo, useRef, useState } from 'react';
import { useTable } from 'react-table';
import { getNewId, isNullOrUndefined, showAlertSnackBar } from 'utils/Global';
import {
  actionButtonsEnable,
  actionButtonStyle,
  iconSize,
} from './ProteinVariant/Constants';
import ImportIngredientError from './ProteinVariant/ImportIngredientError';
import { useProteinFormulationKeyboardHandler } from './hooks';
import { ProteinIngredientsTemplate } from './ProteinIngredientsTemplate';

function calculateMolecularWeightAndConcentration(
  obj: PFDocProteinFormulationIngredientsDTO,
) {
  const mw = isPositiveFloat(obj.molecularWeight);
  const mgmL = isPositiveFloat(obj.concentrationmgmL);
  if (mw && mgmL) {
    obj.concentrationmM =
      obj.molecularWeight > 0
        ? parseFloat(
            (
              parseFloat(obj.concentrationmgmL.toString()) /
              parseFloat(obj.molecularWeight.toString())
            ).toFixed(3),
          )
        : 0;
  }
  return obj;
}

const integerFields = ['id'];
const proteinDimensionFields = ['molecularWeight', 'concentrationmgmL'];
const floatingNumberFields = [...proteinDimensionFields, 'molecularWeight'];

function isPositiveFloat(d) {
  if (parseFloat(d) >= 0) {
    return true;
  }
  return false;
}

const columnProps = {
  disableSort: false,
  editable: true,
  filterDefinition: { type: 'text' } as FmlxColumnFilter,
  headerStyle: {},
  hideFilter: true,
  hideSort: false,
};

type ProteinIngredientsProps = {
  proteinFormulationIngredients: PFDocProteinFormulationIngredientsDTO[];
  pfIngTypes: ProteinFormulationIngredientTypeDTO[];
  updatePfIngs: (
    ings: PFDocProteinFormulationIngredientsDTO[],
    deletedIngs?: PFDocProteinFormulationIngredientsDTO[],
  ) => void;
  setIngTypes: any;
  isReadOnly: boolean;
};
const ProteinIngredients = memo(function ProteinIngredients({
  proteinFormulationIngredients,
  pfIngTypes,
  updatePfIngs,
  isReadOnly,
}: ProteinIngredientsProps) {
  const [proteinIngredients, setProteinIngredients] = useState<
    PFDocProteinFormulationIngredientsDTO[]
  >([]);
  const [selectedProteinIngs, setSelectedProteinIngs] = useState<
    PFDocProteinFormulationIngredientsDTO[]
  >([]);
  const [showImportError, setShowImportError] = useState(false);
  const inputFileRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPasteDisabled, setIsPasteDisabled] = useState<boolean>(false);

  const onRowSelected = (items: any[], ids: string[]) => {
    setSelectedIds(ids.map((i) => parseInt(i)));
    setSelectedProteinIngs(items);
  };

  const onTableSorted = ({
    column,
    type,
  }: {
    column: string;
    type: FmlxTable.SortType;
  }) => {
    if (!proteinIngredients || !proteinIngredients.sort) {
      return;
    }

    const selectedItems = selectedIds.map((i) => proteinIngredients[i]);
    proteinIngredients.sort((a, b) => {
      if (a[column] === b[column]) {
        return 0;
      }
      const result = a[column] > b[column] ? 1 : -1;
      return type === FmlxTable.SortType.ASC ? result : -result;
    });

    const newProteinIngredients = [...proteinIngredients];
    setProteinIngredients(newProteinIngredients);

    const reorderedSelectedRowIds = selectedItems
      .map((item) => newProteinIngredients.indexOf(item))
      .filter((i) => i !== -1);
    setSelectedIds(reorderedSelectedRowIds);
  };

  const copyProteinIndredientsHandler = async () => {
    try {
      const buffer = new IndexDBBuffer();
      const copyType = [
        { value: CopyTypes.ProteinIngredients, documentType: ETreeNodeTypes.Protein },
      ];

      const copiedIngredients = selectedProteinIngs.map((ing) => {
        return {
          ...new PFDocProteinFormulationIngredientsDTO(),
          id: getNewId(),
          name: ing.name,
          concentrationmM: ing.concentrationmM,
          concentrationmgmL: ing.concentrationmgmL,
          lot: ing.lot,
          ingState: PFStateDTO.New,
          proteinFormulationIngredientTypeId: ing.proteinFormulationIngredientTypeId,
          molecularWeight: ing.molecularWeight,
          userId: ing.userId,
        };
      });

      await buffer.writeJson(null, null, copyType, null, copiedIngredients);
      setIsPasteDisabled(false);

      showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Ingredient copied');
    } catch {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Failed to copy protein ingredients.',
      );
    }
  };

  const pasteProteinIngredientsHandler = async () => {
    try {
      const buffer = new IndexDBBuffer();
      const copyType: any = await buffer.readFromIndexedDb(COPTYPESTORE.name);
      const copiedProteinIngredientsJson =
        await buffer.readFromIndexedDb<PFDocProteinFormulationIngredientsDTO>(
          PROTEINSTORE.name,
        );

      if (
        !copiedProteinIngredientsJson?.length ||
        copyType[0]?.value != CopyTypes.ProteinIngredients
      ) {
        showAlertSnackBar(
          EAlertType.INFO,
          ESnackBarPosition.TOP,
          'No ingredients to paste',
        );
        setIsPasteDisabled(true);
        return;
      }

      const copiedProteinIngredients = copiedProteinIngredientsJson.map((raw) => {
        const ing = new PFDocProteinFormulationIngredientsDTO();

        ing.id = raw.id;
        ing.name = raw.name ?? '';
        ing.lot = raw.lot ?? '';
        ing.molecularWeight = raw.molecularWeight;
        ing.concentrationmgmL = raw.concentrationmgmL;
        ing.concentrationmM = raw.concentrationmM;
        ing.userId = raw.userId ?? null;
        ing.ingState = raw.ingState;
        ing.proteinFormulationIngredientTypeId = raw.proteinFormulationIngredientTypeId;

        return ing;
      });

      const updatedIngredients = [...proteinIngredients, ...copiedProteinIngredients];
      setProteinIngredients(updatedIngredients);
      updatePfIngs(updatedIngredients);
      showAlertSnackBar(EAlertType.SUCCESS, ESnackBarPosition.TOP, 'Ingredient pasted');
    } catch {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Failed to paste ingredients',
      );
    }
  };

  useProteinFormulationKeyboardHandler(
    copyProteinIndredientsHandler,
    pasteProteinIngredientsHandler,
  );

  useEffect(() => {
    if (!isNullOrUndefined(proteinFormulationIngredients)) {
      setProteinIngredients(proteinFormulationIngredients);
    }
  }, [proteinFormulationIngredients]);

  useEffect(() => {
    const fetchIndexDbData = async () => {
      const buffer = new IndexDBBuffer();
      const copyType: any[] | null = await buffer.readFromIndexedDb(COPTYPESTORE.name);
      const copiedProteinIngredients: any[] | null = await buffer.readFromIndexedDb(
        PROTEINSTORE.name,
      );

      const hasValidCopyType =
        Array.isArray(copyType) &&
        copyType.length > 0 &&
        copyType[0].value === CopyTypes.ProteinIngredients &&
        copyType[0].documentType === ETreeNodeTypes.Protein;

      const hasCopiedIngredients =
        Array.isArray(copiedProteinIngredients) && copiedProteinIngredients.length > 0;

      setIsPasteDisabled(!(hasValidCopyType && hasCopiedIngredients));
    };
    fetchIndexDbData();
  }, []);

  function AddHandler() {
    const pfIngredient: PFDocProteinFormulationIngredientsDTO =
      new PFDocProteinFormulationIngredientsDTO();
    pfIngredient.id = getNewId();
    pfIngredient.ingState = PFStateDTO.New;
    pfIngredient.proteinFormulationIngredientTypeId = pfIngTypes[0].id;
    proteinIngredients.push(pfIngredient);
    setProteinIngredients([...proteinIngredients]);
    updatePfIngs(proteinIngredients);
  }

  function onInlineEditingChange(key: string, index: number, inputValue: any) {
    const ing = proteinIngredients[index];
    if (!ing) {
      return true;
    }

    if (integerFields.includes(key) || floatingNumberFields.includes(key)) {
      const numericVal = Number(String(inputValue ?? ''));
      const parsedVal = integerFields.includes(key) ? Math.trunc(numericVal) : numericVal;

      ing[key] = isNaN(parsedVal) ? null : parsedVal;
    } else {
      ing[key] = inputValue;
    }
    if (proteinDimensionFields.includes(key)) {
      calculateMolecularWeightAndConcentration(ing);
    }
    if (ing.ingState === PFStateDTO.UnChanged) {
      ing.ingState = PFStateDTO.Modified;
    }

    setSelectedIds([index]);
    setProteinIngredients([...proteinIngredients]);
    updatePfIngs(proteinIngredients);
    return true;
  }

  const onNumericCellChange = useCallback(
    (val: number, id: number, key: string) => {
      const index = proteinIngredients.findIndex((ing) => ing.id == id);
      if (!proteinIngredients[index]) {
        return;
      }
      proteinIngredients[index][key] = val;
      if (proteinDimensionFields.includes(key)) {
        calculateMolecularWeightAndConcentration(proteinIngredients[index]);
      }
      if (proteinIngredients[index].ingState === PFStateDTO.UnChanged) {
        proteinIngredients[index].ingState = PFStateDTO.Modified;
      }
      setSelectedIds([index]);
      setProteinIngredients([...proteinIngredients]);
      updatePfIngs(proteinIngredients);
    },
    [proteinIngredients, setSelectedIds, setProteinIngredients, updatePfIngs],
  );

  function deleteHandler() {
    selectedProteinIngs.forEach((selectedIng) => {
      const idx = proteinIngredients.findIndex((pfIng) => pfIng.id === selectedIng.id);
      proteinIngredients.splice(idx, 1);
    });
    setProteinIngredients([...proteinIngredients]);
    updatePfIngs(proteinIngredients, selectedProteinIngs);
    setSelectedProteinIngs([]);
    setSelectedIds([]);
  }

  function handleImportIng() {
    if (!isNullOrUndefined(inputFileRef)) {
      inputFileRef.current.click();
    }
  }

  function fileChangedHandler(e) {
    setErrorMessage(null);
    let result;
    const file = e.target.files[0];
    const reader = new FileReader();
    const PFPROPNAMES = {
      0: 'proteinFormulationIngredientTypeId',
      1: 'name',
      2: 'molecularWeight',
      3: 'concentrationmgmL',
      4: 'concentrationmM',
    };
    reader.onload = function (event) {
      result = event.target.result;
      try {
        const pfIngs = [];
        const ings = result.split(/\r?\n/);
        if (ings.length <= 1) {
          throw new Error('Error in format');
        } else {
          ings.shift();
        }
        ings.map((ing) => {
          const props = ing.split('\t');
          if (props.length != 5) {
            throw new Error('Error in format');
          }
          const pfIng: PFDocProteinFormulationIngredientsDTO =
            new PFDocProteinFormulationIngredientsDTO();
          props.map((prop, i) => {
            const key = PFPROPNAMES[i];
            if (key === 'proteinFormulationIngredientTypeId') {
              const pfType = pfIngTypes?.find((pfit) => pfit.name === prop)?.id;
              if (pfType !== undefined) {
                pfIng[key] = pfType;
              } else {
                setErrorMessage('Protein type specified in the file does not exist.');
                throw new Error();
              }
            } else if (key === 'name') {
              pfIng[key] = prop;
            } else {
              pfIng[key] = parseFloat(prop);
            }
          });
          pfIng.id = getNewId();
          pfIng.ingState = PFStateDTO.New;
          pfIngs.push(pfIng);
        });
        setProteinIngredients([...proteinIngredients, ...pfIngs]);
        updatePfIngs([...proteinIngredients, ...pfIngs]);
      } catch (e) {
        setShowImportError(true);
        console.error(e);
      }
      e.target.value = '';
    };
    reader.readAsText(file);
  }

  const getButtonsList = () => [
    {
      onClick: handleImportIng,
      isDisabled: isReadOnly,
      title: 'IMPORT',
      titleClass: 'text-[#FFFFFF] text-[1.077rem]',
      titleTip: 'Import Ingredients',
      contentContainerClass: 'w-[100%] h-[1.846rem] bg-[#313131] ',
      mainContainerClass: 'w-[45%]',
    },
    {
      onClick: copyProteinIndredientsHandler,
      isDisabled: (selectedProteinIngs.length > 0 ? false : true) || isReadOnly,
      titleTip: 'Copy',
      iconProps: { name: 'Copy', fill: 'white', ...iconSize },
      contentContainerStyle: {
        ...actionButtonStyle,
        backgroundColor: selectedProteinIngs.length > 0 ? '#006AF5' : '#CACACA',
      },
    },
    {
      onClick: pasteProteinIngredientsHandler,
      isDisabled: isPasteDisabled || isReadOnly,
      titleTip: 'Paste',
      iconProps: { name: 'Paste', fill: 'white', ...iconSize },
      contentContainerStyle: {
        ...actionButtonStyle,
        backgroundColor: isPasteDisabled ? '#CACACA' : '#006AF5',
      },
    },
    {
      onClick: AddHandler,
      isDisabled: isReadOnly,
      titleTip: 'Add',
      iconProps: { name: 'Add', fill: 'white', ...iconSize },
      contentContainerStyle: {
        ...actionButtonStyle,
        backgroundColor: actionButtonsEnable.add,
      },
    },
    {
      onClick: deleteHandler,
      isDisabled: selectedProteinIngs.length === 0 || isReadOnly,
      titleTip: 'Delete',
      iconProps: { name: 'Delete', fill: 'white', ...iconSize },
      contentContainerStyle: {
        ...actionButtonStyle,
        backgroundColor: actionButtonsEnable.delete,
      },
    },
  ];

  const ingTypeDropdownItems = useMemo(
    () =>
      pfIngTypes.map((type) => ({
        group: '',
        id: type.id,
        text: type.name,
      })),
    [pfIngTypes],
  );

  const columns: FmlxColumn[] = useMemo(
    () => [
      {
        Header: 'Type',
        accessor: 'proteinFormulationIngredientTypeId',
        ...columnProps,
        editable: false,
        Cell: (arg) => {
          const { original: ing } = arg.row;
          return (
            <div className="text-left">
              <FmlxDropdown
                id={`proteiningredients-colums-dropdown-${ing.id}`}
                key={`dropdown-proteinFormulationIngredientTypeId-${ing.id}`}
                items={ingTypeDropdownItems}
                selectedItem={ing.proteinFormulationIngredientTypeId}
                menuPosition={FmlxDropdown.MenuPosition.DEFAULT}
                menuProps={{} as any}
                disabled={isReadOnly}
                onChange={(value) =>
                  onNumericCellChange(value, ing.id, 'proteinFormulationIngredientTypeId')
                }
              />
            </div>
          );
        },
      },
      { Header: 'Name', accessor: 'name', ...columnProps },
      { Header: 'ID', accessor: 'userId', ...columnProps },
      { Header: 'Lot', accessor: 'lot', ...columnProps },
      { Header: 'MW(kDa)', accessor: 'molecularWeight', ...columnProps },
      { Header: 'Conc.(mg/mL)', accessor: 'concentrationmgmL', ...columnProps },
      {
        Header: 'Conc.(mM)',
        accessor: 'concentrationmM',
        ...columnProps,
        editable: false,
      },
    ],
    [onNumericCellChange],
  );

  return (
    <>
      <ImportIngredientError
        showImportError={showImportError}
        setShowImportError={setShowImportError}
        errorMessage={errorMessage}
      />
      <div id="proteiningredients-wrapper" className="w-[100%] h-[6.82%] flex flex-row">
        <div
          id="proteiningredients-title"
          className="w-[61%] h-[100%] text-[1.077rem] font-bold text-[#1B1B1B] place-content-center"
        >
          Ingredient
        </div>
        <ProteinIngredientsTemplate width="17%" />
        <div
          id="proteiningredients-content-container"
          className="w-[22%] h-[100%] flex justify-end place-content-center"
        >
          <ButtonsListContainer
            buttonsList={getButtonsList()}
            containerStyleClass="w-[100%] h-[100%] flex justify-between place-content-center"
          />
          <input
            id="proteiningredients-myinput"
            type="file"
            ref={inputFileRef}
            style={{ display: 'none' }}
            onChange={fileChangedHandler}
            accept=".txt"
          />
        </div>
      </div>
      <div
        id="proteiningredients-overflow-table"
        className="w-[100%] overflow-auto h-[91.47%]"
      >
        <FmlxTable
          id="protein-formulation-ing-table"
          selectedRowIds={selectedIds}
          type={FmlxTable.Type.MULTISELECT}
          getSelectedRow={onRowSelected}
          getSortValue={onTableSorted}
          onInlineEditingChange={onInlineEditingChange}
          columns={columns}
          data={proteinIngredients}
          showToolbar={false}
          placeholder={<></>}
        />
      </div>
    </>
  );
});

export default ProteinIngredients;

export function Table({ columns, data, cellProps = (row) => ({}), classNames = '' }) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  return (
    <table
      id="proteiningredients-table-wrapper"
      className={'protein-table ' + classNames}
      {...getTableProps()}
      style={{ overflow: 'auto', width: '100%' }}
    >
      <thead id="proteiningredients-table-header">
        {headerGroups.map((headerGroup, index) => (
          <tr
            id={`proteiningredients-header-row-${index}`}
            key={`hgc${index}`}
            style={{ display: 'flex' }}
            {...headerGroup.getHeaderGroupProps()}
          >
            {headerGroup.headers.map((column, i) => (
              <th
                id={`protein-ingredients-header-cell-${index}-${i}`}
                key={`hgh${i}`}
                {...column.getHeaderProps(cellProps(column))}
                style={{ flex: column.width }}
              >
                {column.render('Header')}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody id="proteiningredients-table-body" {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr
              id={`proteiningredients-table-body-row-${i}`}
              key={`rowC${i}`}
              style={{ display: 'flex' }}
              {...row.getRowProps()}
            >
              {row.cells.map((cell, index) => {
                return (
                  <td
                    id={`proteiningredients-table-body-cell-${i}-${index}`}
                    key={`rCel${index}`}
                    {...cell.getCellProps(cellProps(row))}
                    style={{ flex: cell.column.width }}
                  >
                    {cell.render('Cell')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
